import { useEffect, useRef, useState } from 'react';
import { useQuery } from 'react-query';
import get from 'lodash/get';
import upperFirst from 'lodash/upperFirst';
import dayjs from 'dayjs';
import { useFormikContext } from 'formik';
import { Tooltip } from 'flowbite-react';
import Button from 'modules/v2/common/AtomicDesign/atoms/Button';

import { ROUTE_REFERRAL_MARKETING_MAGAZINE_EDITOR } from 'modules/v2/routes/constants';
import { getGeneratedMagazine, getGeneratedMagazineMonthUrl } from 'modules/api';
import { Link } from 'modules/v2/draft/components';
import { Loader } from 'modules/v2/common/components';

import { formatDateTime } from 'modules/v2/common/utils';
import { isAdminEditing } from 'modules/v2/utils';
import QuestionCircle from 'modules/v2/common/components/SvgIcon/QuestionCircle';
import {
  magazineStatus,
  monthsList,
  params as defaultParams,
  rmMagazineStatus,
} from '../../constants';
import StatusHeader from './StatusHeader';
import {
  MAGAZINE_STATES,
  getMagazineState,
  isNotFoundError,
  retryUnlessNotFound,
} from './magazineState';

const SUPPORT_URL = 'https://authorify.com/contact-us/';

// The page asks again only while a publish is running, or its online page
// reads ERROR (that ERROR may be left over from an earlier publish): every
// 3 s, for at most 5 minutes. Every other state is read once, on load.
export const PUBLISH_POLL_INTERVAL_MS = 3000;
export const PUBLISH_POLL_WINDOW_MS = 5 * 60 * 1000;

const SupportLink = ({ children }) => (
  <a href={SUPPORT_URL} target="_blank" rel="noreferrer" className="font-bold underline">
    {children}
  </a>
);

const ContactSupportButton = () => (
  <Button type="outlined" href={SUPPORT_URL} target="_blank" rel="noreferrer">
    Contact support
  </Button>
);

// The file the member's latest generation made (a preview, or what was sent to
// print), shown the way the editor shows its preview.
const MagazineFile = ({ title, url }) =>
  url ? (
    <div className="w-full bg-white rounded-lg p-6 shadow-box">
      <div className="text-neutral-800 text-base font-semibold mb-3.5">{title}</div>
      <div className="h-550">
        <afy-flip-book key="active-magazine-file-pdf" pdfurl={url} />
      </div>
    </div>
  ) : null;

// The query's last answer. react-query v3 puts a query that has no data back
// to `loading` (and clears its error) on every refetch, so a 404 would read as
// "loading" again on each check; the page keeps the last answer instead. A
// failed check other than a 404 keeps the last good answer (data or a 404), so
// one network blip during a publish does not stop the checks.
const useLastAnswer = (query) => {
  const last = useRef({ data: undefined, error: null, settled: false });
  if (query.status === 'success') {
    last.current = { data: query.data, error: null, settled: true };
  } else if (query.status === 'error') {
    const hadGoodAnswer = last.current.data !== undefined || isNotFoundError(last.current.error);
    const isBlip = !isNotFoundError(query.error) && hadGoodAnswer;
    if (!isBlip) last.current = { data: query.data, error: query.error, settled: true };
  }
  return last.current;
};

const GeneratedMagazine = ({ history }) => {
  const { setFieldValue } = useFormikContext();
  const [isPolling, setIsPolling] = useState(false);
  const [isPollWindowOver, setIsPollWindowOver] = useState(false);
  const params = defaultParams;
  // VITE_RMM_CURRENT_MONTH is a lowercase month name; the API and the editor
  // route keep the raw value.
  const issue = `${upperFirst(params.month)} ${params.year}`;

  const queryOptions = {
    retry: retryUnlessNotFound,
    refetchInterval: isPolling ? PUBLISH_POLL_INTERVAL_MS : false,
    refetchOnWindowFocus: false,
  };

  // Answered only once the online page of the magazine is DONE.
  const publishedQuery = useQuery(
    ['getGeneratedMagazineMonthUrl', params.year, params.month],
    getGeneratedMagazineMonthUrl,
    queryOptions,
  );
  // The member's latest preview or publish run for this issue.
  const latestQuery = useQuery(
    ['activeMagazineLatestGeneration', params.year, params.month],
    () => getGeneratedMagazine(params.year, params.month),
    queryOptions,
  );

  const published = useLastAnswer(publishedQuery);
  const latest = useLastAnswer(latestQuery);
  const answeredState = getMagazineState({ published, latest });

  // A page ERROR can be left over from an earlier publish: once this page has
  // seen the run publishing, it keeps saying "Publishing" until the 5-minute
  // window is over, and only then shows the online-page problem.
  const sawPublishing = useRef(false);
  if (answeredState === MAGAZINE_STATES.PUBLISHING) sawPublishing.current = true;
  const state =
    answeredState === MAGAZINE_STATES.PAGE_ERROR && sawPublishing.current && !isPollWindowOver
      ? MAGAZINE_STATES.PUBLISHING
      : answeredState;
  const isWatching =
    answeredState === MAGAZINE_STATES.PUBLISHING || answeredState === MAGAZINE_STATES.PAGE_ERROR;

  useEffect(() => {
    setIsPolling(isWatching && !isPollWindowOver);
  }, [isWatching, isPollWindowOver]);

  // Starts the 5-minute window when a publish (or a page ERROR) shows up (and
  // again after "Check again"); the window closes when it runs out.
  useEffect(() => {
    if (!isWatching) {
      setIsPollWindowOver(false);
      return undefined;
    }
    if (isPollWindowOver) return undefined;
    const timer = setTimeout(() => setIsPollWindowOver(true), PUBLISH_POLL_WINDOW_MS);
    return () => clearTimeout(timer);
  }, [isWatching, isPollWindowOver]);

  const checkAgain = () => {
    setIsPollWindowOver(false);
    publishedQuery.refetch();
    latestQuery.refetch();
  };

  const generatedMagazine = published.data;
  const latestGeneration = latest.data;
  const currentMagazineStatus = get(generatedMagazine, 'magazine.status');
  const sendToPrint = currentMagazineStatus === rmMagazineStatus.SENT_FOR_PRINTING;

  const handleEditMagazine = () => {
    setFieldValue('hasPreviousData', true);

    const parameters = {
      month: monthsList[get(generatedMagazine, 'magazine.month')],
      year: get(generatedMagazine, 'magazine.year'),
    };

    const routeToRedirect = ROUTE_REFERRAL_MARKETING_MAGAZINE_EDITOR.replace(
      ':month',
      parameters.month,
    ).replace(':year', parameters.year);

    return history.push(routeToRedirect);
  };

  // Opens the editor for the issue this page queried (with no magazine there is
  // no `magazine.month`/`year` to read).
  const handleOpenEditor = () => {
    setFieldValue('hasPreviousData', true);

    const routeToRedirect = ROUTE_REFERRAL_MARKETING_MAGAZINE_EDITOR.replace(
      ':month',
      params.month.toLowerCase(),
    ).replace(':year', params.year);

    return history.push(routeToRedirect);
  };

  const isMagazineSentToPrinting = isAdminEditing()
    ? false
    : currentMagazineStatus === rmMagazineStatus.SENT_FOR_PRINTING;

  const openEditorButton = (label) => (
    <Button type="primary" buttonType="button" onClick={handleOpenEditor}>
      {label}
    </Button>
  );

  const lastCheckedAt = Math.max(latestQuery.dataUpdatedAt, latestQuery.errorUpdatedAt);

  const renderHeader = () => {
    switch (state) {
      case MAGAZINE_STATES.NOT_STARTED:
        return (
          <StatusHeader
            tone="neutral"
            pill="Not started"
            title={`You haven't started your ${issue} magazine`}
            action={openEditorButton('Personalize magazine')}
          >
            Personalize it and publish it to send it to print.
          </StatusHeader>
        );
      case MAGAZINE_STATES.SAVED:
        return (
          <StatusHeader
            tone="warning"
            pill="Not published"
            title="Your changes are saved"
            action={openEditorButton('Open the editor')}
          >
            Publish it from the editor to send it to print and put it online.
          </StatusHeader>
        );
      case MAGAZINE_STATES.PREVIEW_RUNNING:
        return (
          <StatusHeader
            tone="warning"
            pill="Not published"
            title="Your preview is being made in the editor"
            action={openEditorButton('Open the editor')}
          >
            When it looks good, publish it from the editor.
          </StatusHeader>
        );
      case MAGAZINE_STATES.PREVIEW_READY:
        return (
          <StatusHeader
            tone="warning"
            pill="Not published"
            title="Your preview is ready, but not published"
            action={openEditorButton('Open the editor to publish')}
          >
            It is not printed and not online yet.
          </StatusHeader>
        );
      case MAGAZINE_STATES.SENT_TO_PRINT_NO_PAGE:
        return (
          <StatusHeader
            tone="success"
            pill="Sent to print"
            title={`Your ${issue} magazine was sent to print`}
          >
            The online version isn&apos;t available for this issue. If you need it,{' '}
            <SupportLink>contact support</SupportLink>.
          </StatusHeader>
        );
      case MAGAZINE_STATES.PUBLISHING:
        return isPollWindowOver ? (
          <StatusHeader
            tone="warning"
            pill="Taking longer"
            title="Publishing is taking longer than usual"
            action={
              <Button type="outlined" buttonType="button" onClick={checkAgain}>
                Check again
              </Button>
            }
          >
            Last checked {dayjs(lastCheckedAt).format('h:mm a')}. If it isn&apos;t here tomorrow,{' '}
            <SupportLink>contact support</SupportLink>.
          </StatusHeader>
        ) : (
          <StatusHeader
            tone="info"
            pill="Publishing"
            spinning
            title="We're publishing your magazine"
          >
            This usually takes a minute or two. You can leave this page.
          </StatusHeader>
        );
      case MAGAZINE_STATES.RENDER_ERROR:
        return (
          <StatusHeader
            tone="error"
            pill="Problem"
            title="We couldn't prepare your magazine"
            action={<ContactSupportButton />}
          >
            Your changes are saved. Support can run it again for you.
          </StatusHeader>
        );
      case MAGAZINE_STATES.PAGE_ERROR:
        return (
          <StatusHeader
            tone="error"
            pill="Problem"
            title="The online version couldn't be created"
            action={<ContactSupportButton />}
          >
            If your magazine was sent to print, that is not affected. Support can fix the online
            version.
          </StatusHeader>
        );
      case MAGAZINE_STATES.PUBLISHED:
        return (
          <StatusHeader
            tone="success"
            pill={sendToPrint ? 'Sent to print' : 'Published'}
            title={`Your ${issue} magazine is published`}
          >
            {sendToPrint
              ? 'It was sent to print. The online version and links are below.'
              : 'The online version and links are below.'}
          </StatusHeader>
        );
      default:
        return (
          <StatusHeader
            tone="error"
            pill="Problem"
            title="We couldn't check your magazine"
            action={
              <Button type="outlined" buttonType="button" onClick={checkAgain}>
                Check again
              </Button>
            }
          >
            Please try again in a moment.
          </StatusHeader>
        );
    }
  };

  if (state === MAGAZINE_STATES.LOADING) {
    return (
      <div className="relative w-full h-full min-h-3/4 overflow-hidden">
        <div className="text-neutral-800 text-2xl font-bold leading-7 mb-6">Active Magazine</div>
        <Loader
          height="60vh"
          title="Loading your magazine..."
          description="Please wait while we load your magazine."
        />
      </div>
    );
  }

  if (state !== MAGAZINE_STATES.PUBLISHED) {
    return (
      <div className="relative w-full h-full min-h-3/4 overflow-hidden">
        <div className="text-neutral-800 text-2xl font-bold leading-7 mb-6">Active Magazine</div>
        {renderHeader()}
        {state === MAGAZINE_STATES.PREVIEW_READY && (
          <MagazineFile title="Your preview" url={get(latestGeneration, 'url')} />
        )}
        {state === MAGAZINE_STATES.SENT_TO_PRINT_NO_PAGE && (
          <MagazineFile title="The printed file" url={get(latestGeneration, 'url')} />
        )}
      </div>
    );
  }

  return (
    <>
      <div className="relative w-full h-full min-h-3/4 overflow-hidden">
        <div className="text-neutral-800 text-2xl font-bold leading-7 mb-6">Active Magazine</div>
        {renderHeader()}
        <div className="w-full h-full flex gap-4 max-[1280px]:flex-wrap">
          <div className="content w-3/4 max-[1280px]:w-full h-full bg-white rounded-lg p-6 shadow-box">
            <div className="relative w-full flex justify-between items-center mb-3.5 max-[500px]:flex-col">
              <div className="text-neutral-800 text-base font-semibold">
                Your {params.month} {params.year} magazine
              </div>
              <Button
                color="light"
                onClick={handleEditMagazine}
                // Changes after printing are not printed: support handles them.
                disabled={isMagazineSentToPrinting}
              >
                Edit magazine
              </Button>
            </div>
            <div className="h-550">
              <afy-flip-book
                key="magazine-step-pdf"
                pdfurl={generatedMagazine?.contentUrl || generatedMagazine?.url}
              />
            </div>
          </div>
          <div className="w-1/3 max-[1280px]:w-full flex flex-col gap-4 max-[1280px]:flex-row">
            <div className="w-full flex flex-col gap-6">
              <div className="w-full flex flex-col items-start gap-5 bg-white shadow-box rounded-lg self-stretch p-6">
                <div className="w-full flex gap-4 justify-between items-center">
                  <div className="text-neutral-800 text-base font-semibold">Status</div>
                  <div
                    className={`flex justify-center items-center ${
                      sendToPrint
                        ? 'bg-success-50 text-success-600'
                        : 'bg-secondary-orange-50 text-secondary-orange-600'
                    } px-3 py-1 rounded-md`}
                  >
                    <span className="font-semibold">{magazineStatus[currentMagazineStatus]}</span>
                  </div>
                </div>
                <div className="border-t w-full flex flex-col items-center bg-white shadow-box rounded-lg" />
                <div className="w-full flex gap-4 justify-between items-center">
                  <div className="text-neutral-800 text-base font-semibold">Last Edited</div>
                  <div className="overflow-hidden text-neutral-400 text-ellipsis  text-sm font-medium">
                    {formatDateTime(generatedMagazine?.updatedAt)}
                  </div>
                </div>
              </div>
              <div className="w-full flex flex-col items-start gap-10 bg-white shadow-box rounded-lg self-stretch p-6">
                <div className="w-full">
                  <h5 className="text-neutral-800 text-sm font-semibold flex gap-1 items-center">
                    Lead capture website
                    <Tooltip
                      content="Generate more leads and grow your business. You'll get notified instantly whenever you receive a new lead."
                      Style="dark"
                      className="max-w-[250px] text-sm text-neutral-200"
                    >
                      <QuestionCircle />
                    </Tooltip>
                  </h5>
                  <Link key="landingPage" id="landingPage" href={generatedMagazine?.pageUrl} />
                </div>
                <div className="w-full">
                  <h5 className="text-neutral-800 text-sm font-semibold flex gap-1 items-center">
                    Digital magazine
                    <Tooltip
                      content="Easily showcase your expertise and build trust with clients who are unable to meet with you in person."
                      Style="dark"
                      className="max-w-[250px] text-sm text-neutral-200"
                    >
                      <QuestionCircle />
                    </Tooltip>
                  </h5>
                  <Link
                    key="digitalMagazine"
                    id="digitalMagazine"
                    href={generatedMagazine?.bookUrl}
                  />
                </div>
                {sendToPrint && (
                  <div className="w-full">
                    <h5 className="text-neutral-800 text-sm font-semibold flex gap-1 items-center">
                      Flipping book
                      <Tooltip
                        content="Your digital magazine flipping book"
                        Style="dark"
                        className="max-w-[250px] text-sm text-neutral-200"
                      >
                        <QuestionCircle />
                      </Tooltip>
                    </h5>
                    <Link
                      key="digitalMagazine"
                      id="digitalMagazine"
                      href={generatedMagazine?.flippingBookUrl}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default GeneratedMagazine;
